import React from 'react';
import { Row, Col, Card, Typography, Empty, Checkbox } from 'antd';
import { EventFormState, SportLiteType } from '../../../../types/types';
import styled from 'styled-components';
import { COLORS } from '../../../../utils/constants';
import Spinner from '../../../common/Spinner';

const { Title, Text } = Typography;

interface Props {
  formState: EventFormState;
  setFormState: React.Dispatch<React.SetStateAction<EventFormState>>;
  errors?: Record<string, string>;
  sportsData: SportLiteType[];
  isSportsLoading: boolean;
}

const Step2Sports: React.FC<Props> = ({
  formState,
  setFormState,
  errors,
  sportsData,
  isSportsLoading,
}) => {
  const emptySportsImage = '';

  const toggleSport = (id: number) => {
    setFormState((prev) => {
      const isSelected = prev.selectedSports.includes(id);

      const updatedSelectedSports = isSelected
        ? prev.selectedSports.filter((sid) => sid !== id)
        : [...prev.selectedSports, id];

      const updatedTournaments = { ...prev.tournaments };
      if (isSelected) {
        delete updatedTournaments[id];
      }

      return {
        ...prev,
        selectedSports: updatedSelectedSports,
        tournaments: updatedTournaments,
      };
    });
  };

  if (isSportsLoading) {
    return (
      <LoadingContainer>
        <Spinner />
        <Text>Loading sports...</Text>
      </LoadingContainer>
    );
  }

  if (!sportsData || sportsData.length === 0) {
    return (
      <WrappingContainer>
        <Empty description="No sports available" />
      </WrappingContainer>
    );
  }

  return (
    <WrappingContainer>
      <Header>
        <Title level={4} style={{ marginBottom: 4 }}>
          Select Sports
        </Title>
        <Text type="secondary">
          Choose all sports you want to include in your event
        </Text>
        {errors?.selectedSports && (
          <ErrorText type="danger">{errors.selectedSports}</ErrorText>
        )}
      </Header>

      <StyledRow gutter={[16, 16]}>
        {sportsData.map((sport) => {
          const selected =
            sport.sportId !== undefined &&
            formState.selectedSports.includes(sport.sportId);

          return (
            <Col key={sport.sportId} xs={24} sm={12} md={8} lg={6}>
              <SportCard
                hoverable
                $selected={selected}
                onClick={() => toggleSport(sport.sportId)}
              >
                <CheckboxRow onClick={(e) => e.stopPropagation()}>
                  <StyledCheckbox
                    checked={selected}
                    onChange={() => toggleSport(sport.sportId)}
                  />
                  <SportName>{sport.sportName}</SportName>
                </CheckboxRow>

                {(sport.sportImageUrl || emptySportsImage) && (
                  <ImageWrapper>
                    <SportImage
                      src={sport.sportImageUrl || emptySportsImage}
                      alt={sport.sportName}
                    />
                  </ImageWrapper>
                )}
              </SportCard>
            </Col>
          );
        })}
      </StyledRow>
    </WrappingContainer>
  );
};

const SportCard = styled(Card)<{ $selected: boolean }>`
  width: 100%;
  background-color: ${({ $selected }) =>
    $selected ? COLORS.skyBlue.light : COLORS.grey.extraLight};
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 16px;
  box-shadow: ${({ $selected }) =>
    $selected
      ? '0 0 0 2px rgba(22,119,255,0.2)'
      : '0 1px 2px rgba(0,0,0,0.08)'};
  position: relative;
  height: 160px;
  overflow: hidden;

  .ant-card-body {
    padding: 0;
  }
`;

const CheckboxRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
  padding: 12px;
  padding-bottom: 0;
  gap: 6px;
`;

const SportName = styled(Text)`
  font-size: 14px;
  color: ${COLORS.black.primary};
  font-weight: 500;
`;

const ImageWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 95px;
  height: 95px;
  overflow: hidden;
`;

const SportImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
`;

const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox-inner {
    width: 16px;
    height: 16px;
  }

  &.ant-checkbox-wrapper .ant-checkbox-checked .ant-checkbox-inner {
    border-color: ${COLORS.skyBlue.primary};
    background-color: ${COLORS.skyBlue.primary};
  }
`;

export const LoadingContainer = styled.div`
  padding: 24px;
  text-align: center;
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

const WrappingContainer = styled.div`
  padding: 24px;
`;

const Header = styled(Col)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
`;

const ErrorText = styled(Text)`
  display: block;
  margin-top: 8px;
`;

const StyledRow = styled(Row)`
  margin-top: 20px;
`;

export default Step2Sports;
